import { IconButton, Link, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { useTranslations } from "adapters/i18n";
import { CustomTableColumn } from "features/shared/components";
import React, { useMemo } from "react";
import { DATE_FORMATS, DateUtils } from "shared/dateUtils";
import type { EnrichedHerdRecord } from "shared/types";
import {
  getLocalCarcassYield,
  MovementListTableColumn,
  RecordType,
} from "../utils/movements.utils";

type UseMovementListTableProps = {
  isFormPanelOpen: boolean;
  onConfirmRequest: (row: EnrichedHerdRecord) => void;
  onEditClick: (row: EnrichedHerdRecord) => void;
  onDeleteRequest: (row: EnrichedHerdRecord) => void;
};

export function useMovementListTable({
  isFormPanelOpen,
  onConfirmRequest,
  onEditClick,
  onDeleteRequest,
}: UseMovementListTableProps) {
  const { t } = useTranslations();

  const recordTypeMap: Record<RecordType, string> = useMemo(
    () => ({
      [RecordType.Entry]: t("movements:entry"),
      [RecordType.Exit]: t("movements:exit"),
    }),
    [t]
  );

  const columns = useMemo<
    CustomTableColumn<EnrichedHerdRecord, typeof MovementListTableColumn>[]
  >(
    () => [
      {
        id: MovementListTableColumn.AnimalCategory,
        label: t("animalCategory:category"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.animalCategory?.name ?? null,
        renderCell: (value) => value ?? "-",
      },
      {
        id: MovementListTableColumn.RecordType,
        label: t("movements:movement"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => recordTypeMap[row.recordTypeId as RecordType],
        renderCell: (value, row) => (
          <div style={{ display: "flex", justifyContent: "left" }}>
            <FiberManualRecordIcon
              style={{
                width: "20px",
                height: "20px",
                paddingRight: "4%",
                color:
                  row.recordTypeId === RecordType.Entry ? "#4fd26b" : "#e98e4f",
              }}
            />

            <Typography style={{ width: "65px" }}>{value}</Typography>
          </div>
        ),
      },
      {
        id: MovementListTableColumn.Quantity,
        label: t("common:amount"),
        isSortable: true,
        type: "number",
        getRowValue: (row) => row.quantity,
      },
      {
        id: MovementListTableColumn.OperationDate,
        label: t("common:fields.date"),
        isSortable: true,
        type: "text",
        getRowValue: (row) =>
          DateUtils.utc(row.operationDate).format(DATE_FORMATS["DD/MM/YYYY"]),
      },
      {
        id: MovementListTableColumn.OperationType,
        label: t("movements:movement-type-short"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.operationType?.name ?? null,
        renderCell: (value) => value ?? "-",
      },
      {
        id: MovementListTableColumn.Weight,
        label: t("livestock:measured-weight"),
        isSortable: true,
        type: "number",
        getRowValue: (row) => row.weight,
        renderCell: (value) =>
          value != null
            ? t("common:measure.x-kg", { value })
            : "-",
      },
      {
        id: MovementListTableColumn.CarcassYield,
        label: t("livestock:carcass-yield"),
        align: "center",
        isSortable: true,
        type: "number",
        getRowValue: (row) => row.carcassYield,
        renderCell: (value) =>
          value != null ? `${getLocalCarcassYield(value)}%` : "-",
      },
      {
        id: MovementListTableColumn.Edit,
        label: t("common:action"),
        isSortable: false,
        type: "custom",
        isActionColumn: true,
        renderCell: (row) => (
          <EditCell
            onConfirmRequest={onConfirmRequest}
            onEditClick={onEditClick}
            row={row}
          />
        ),
      },
      {
        id: MovementListTableColumn.Delete,
        label: t("common:actions.delete"),
        isSortable: false,
        type: "custom",
        isActionColumn: true,
        renderCell: (row) => (
          <DeleteCell
            isFormPanelOpen={isFormPanelOpen}
            onDeleteRequest={onDeleteRequest}
            row={row}
          />
        ),
      },
    ],
    [
      isFormPanelOpen,
      onConfirmRequest,
      onDeleteRequest,
      onEditClick,
      recordTypeMap,
      t,
    ]
  );

  return { columns };
}

type EditCellProps = {
  onConfirmRequest: (row: EnrichedHerdRecord) => void;
  onEditClick: (row: EnrichedHerdRecord) => void;
  row: EnrichedHerdRecord;
};

function EditCell({ onConfirmRequest, onEditClick, row }: EditCellProps) {
  const { t } = useTranslations();

  const testId = row.confirmed
    ? "edit-movement-button"
    : "confirm-movement-button";

  const label = row.confirmed
    ? t("common:actions.edit").toUpperCase()
    : t("common:actions.confirm");

  const handleClick = () => {
    if (row.confirmed) {
      onEditClick(row);
      return;
    }

    onConfirmRequest(row);
  };

  return (
    <Link
      component={"button" as never}
      data-testid={testId}
      onClick={handleClick}
      style={{ fontWeight: "500" }}
    >
      {label.toUpperCase()}
    </Link>
  );
}

type DeleteCellProps = {
  isFormPanelOpen: boolean;
  onDeleteRequest: (row: EnrichedHerdRecord) => void;
  row: EnrichedHerdRecord;
};

function DeleteCell({
  isFormPanelOpen,
  onDeleteRequest,
  row,
}: DeleteCellProps) {
  const { t } = useTranslations();

  const handleClick = () => {
    onDeleteRequest(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.delete")}
      data-testid="delete-movement-button"
      disabled={isFormPanelOpen}
      onClick={handleClick}
    >
      <DeleteIcon />
    </IconButton>
  );
}
